import React, { useEffect, useState } from 'react';
import type { CSSProperties } from 'react';
import { Collapse, theme, Button } from 'antd';
import type { CollapseProps } from 'antd';
import { PlusOutlined, MinusOutlined, FilePdfOutlined } from '@ant-design/icons';
import InspectionAssetTabContent from './InspectionAssetTabContent';
import InspectionDamageTabContent from './InspectionDamageTabContent';
import DamageCountView from '../../../components/DamageCountView';
import { dateFormatter } from '../../../utils';
import useInspectionAssets from '../../../hooks/useInspectionAssets';
import type { VinInspectionItem } from '../inspectionTypes';
import { getInspectionActiveDamageCount, getInspectionPanelKey } from '../inspectionUtils';
import PdfContainer from '../../../components/Pdf/PdfContainer';
import { CountIndicator } from '../../../components/CellRenderers/CountCellRenderer';
import { useDamages } from '../../../hooks/useDamages';
import type { ApiError } from '../../../types/api/network';
import type { InspectionMediaData, InspectionMediaType } from '../inspectionMediaTypes';
import {
  ALL_INSPECTION_TAB_KEYS,
  DEFAULT_INSPECTION_TAB,
  getAssetTabType,
  INSPECTION_TABS,
  isAssetTab,
  resolveInspectionTab,
  type InspectionTabKey,
} from '../inspectionTabs';

export type TabKey = InspectionTabKey;

interface InspectionDetailsProps {
  inspection: VinInspectionItem;
  index: number;
  totalInspections: number;
  expandedPanels: string[];
  onExpandChange?: (panelKey: string, expanded: boolean) => void;
  visibleTabs?: InspectionTabKey[];
  onOpenMedia?: (
    inspection: VinInspectionItem,
    imageData?: InspectionMediaData,
    type?: InspectionMediaType,
    activeTab?: InspectionTabKey
  ) => void;
}

const InspectionDetails: React.FC<InspectionDetailsProps> = ({
  inspection,
  index,
  totalInspections,
  expandedPanels,
  onExpandChange,
  visibleTabs = ALL_INSPECTION_TAB_KEYS,
  onOpenMedia,
}) => {
  const { token } = theme.useToken();
  const panelKey = getInspectionPanelKey(inspection, index);

  const [activeTab, setActiveTab] = useState<InspectionTabKey>(DEFAULT_INSPECTION_TAB);
  const [userSelectedTab, setUserSelectedTab] = useState(false);
  const [pdf, setPdf] = useState<boolean>(false);

  const isPanelActive = expandedPanels.includes(panelKey);
  const availableTabs = INSPECTION_TABS.filter(tab => visibleTabs.includes(tab.key));
  const isAssetTabActive = isAssetTab(activeTab);
  const shouldLoadAssets = isPanelActive && isAssetTabActive;

  const { assets, loadingAssets, assetsLoaded, error: assetError, fetchAssets } = useInspectionAssets({
    inspectionId: inspection?.inspectionId,
    enabled: shouldLoadAssets,
  });
  const {
    damages,
    loading: damageLoading,
    error: damageError,
    fetchDamages,
  } = useDamages({
    inspectionId: inspection?.inspectionId,
    // Only fetch damages for expanded panels. Collapsed panels use
    // inspection.damageCount / inspection.damages for header badges.
    enabled: isPanelActive,
  });
  // Expanded panels prefer the live damages query (already excludes soft-deletes).
  // Collapsed panels count from the list payload — never fall back to damageCount
  // when a damages array exists, or deleted rows inflate the badge again.
  const damageCount = getInspectionActiveDamageCount(
    inspection,
    isPanelActive && !damageLoading ? damages : null
  );
  const hasDamages = damageCount > 0;

  const panelStyle: CSSProperties = {
    borderRadius: token.borderRadiusLG,
    border: 'none',
    background: '#ffffff',
  };

  useEffect(() => {
    setUserSelectedTab(false);
  }, [inspection?.inspectionId]);

  useEffect(() => {
    const nextTab = resolveInspectionTab({
      hasDamages,
      activeTab,
      userSelectedTab,
      visibleTabs,
    });

    if (nextTab !== activeTab) {
      setActiveTab(nextTab);
    }
  }, [hasDamages, activeTab, userSelectedTab, visibleTabs]);

  const handleTabClick = (tab: InspectionTabKey) => {
    if (!visibleTabs.includes(tab)) return;
    setUserSelectedTab(true);
    setActiveTab(tab);

    if (!isPanelActive && onExpandChange && panelKey) {
      onExpandChange(panelKey, true);
    }
  };

  const handlePanelChange = (keys: string | string[]) => {
    const keyArray = Array.isArray(keys) ? keys : [keys];

    if (onExpandChange && panelKey) {
      const isNowExpanded = keyArray.includes(panelKey);
      onExpandChange(panelKey, isNowExpanded);
    }
  };

  const handleDamagePdf = () => {
    setPdf(true);
  };

  const tabStyle = (active: boolean): CSSProperties => ({
    cursor: 'pointer',
    borderBottom: active ? `2px solid ${token.colorPrimary}` : '2px solid transparent',
  });

  const handleOpenModal = (imageData?: InspectionMediaData, type?: InspectionMediaType) => {
    onOpenMedia?.(inspection, imageData, type, activeTab);
  };

  const renderContent = () => (
    <>
      <div style={{ display: activeTab === 'defectImages' ? 'block' : 'none' }}>
        <InspectionDamageTabContent
          inspection={inspection}
          damages={damages}
          loading={damageLoading}
          error={damageError as ApiError | undefined}
          onRetry={() => {
            fetchDamages().catch(() => undefined);
          }}
          onOpenModal={onOpenMedia ? handleOpenModal : undefined}
        />
      </div>

      {visibleTabs.some(isAssetTab) && (
        <div style={{ display: isAssetTabActive ? 'block' : 'none' }}>
          <InspectionAssetTabContent
            onOpenModal={onOpenMedia ? handleOpenModal : undefined}
            inspection={inspection}
            assetType={getAssetTabType(activeTab)}
            assets={assets}
            loadingAssets={loadingAssets}
            assetsLoaded={assetsLoaded}
            error={assetError}
            onRetry={() => fetchAssets(inspection.inspectionId)}
          />
        </div>
      )}
    </>
  );

  const renderExtraTabs = () => (
    <div onClick={e => e.stopPropagation()} className="flex gap-3 items-center">
      <DamageCountView value={damageCount} />
      {availableTabs.length > 1 &&
        availableTabs.map(tab => {
          const isTabSelected = isPanelActive && activeTab === tab.key;
          return (
            <span
              key={tab.key}
              style={tabStyle(isTabSelected)}
              onClick={() => handleTabClick(tab.key)}
              className={`px-1 py-1 transition-colors duration-200 hover:text-primary-600 text-sm ${isTabSelected ? '' : 'text-gray-400'}`}>
              {tab.label}
            </span>
          );
        })}
    </div>
  );

  const items: CollapseProps['items'] = [
    {
      key: panelKey,
      label: (
        <div className="flex items-center gap-2">
          <span className="flex font-medium">
            <div className="text-gray-750 font-bold me-2">{dateFormatter(inspection.dateTime) || 'N/A'}</div>
            <CountIndicator value={totalInspections} size={25} />
            <div className="ms-2 text-sm text-gray-600">
              {inspection.location || 'N/A'} / {inspection.terminal || 'N/A'}
            </div>
          </span>

          {damageCount > 0 && (
            <Button
              type="default"
              size="small"
              icon={<FilePdfOutlined />}
              title="Generate Damage PDF"
              aria-label="Generate Damage PDF"
              onClick={e => {
                e.stopPropagation();
                e.preventDefault();
                handleDamagePdf();
              }}>
              PDF
            </Button>
          )}
        </div>
      ),
      children: renderContent(),
      style: panelStyle,
      extra: renderExtraTabs(),
    },
  ];

  const collapseStyle: React.CSSProperties = {
    '--ant-collapse-borderless-content-padding': '0',
    background: '#fff0e9',
    borderLeft: isPanelActive ? `5px solid ${token.colorPrimary}` : '',
  } as React.CSSProperties;

  return (
    <>
      {pdf && <PdfContainer inspection={inspection} />}
      <Collapse
        bordered={false}
        activeKey={expandedPanels}
        onChange={handlePanelChange}
        expandIcon={({ isActive }) => (isActive ? <MinusOutlined className="text-gray-500" /> : <PlusOutlined className="text-gray-500" />)}
        items={items}
        style={collapseStyle}
      />
    </>
  );
};

export default InspectionDetails;
